<template>
  <a-modal
    style="z-index: 1001;"
    class="login_modal"
    v-model:open="visible"
    title="Choose your role and log in"
    @cancel="handleCancel"
    :footer="null"
  >
    <div class="tip">
      <span>Don't have an account ?</span>
      <router-link to="/" class="link" @click.prevent="openRegisterModal">Sign Up</router-link>
    </div>
    <a-tabs v-model:activeKey="activeTab">
      <a-tab-pane key="seeker" tab="Job hunting"></a-tab-pane>
      <a-tab-pane key="recruiter" tab="Recruiting"></a-tab-pane>
    </a-tabs>
    <a-form class="modal_form" ref="formRef" :model="formState">
      <EmailInputItem @change-email="handleChangeEmail" nameProp="loginName" />
      <a-form-item
        validateTrigger="blur"
        label=""
        name="password"
        :rules="[{ required: true, message: 'Enter password' }]"
      >
        <a-input-password
          v-model:value="formState.password"
          placeholder="Enter password"
          :autocomplete="isEdge ? 'new-password' : ''"
          :visibilityToggle="true"
        />
      </a-form-item>
      <div class="login_options">
        <span class="custom_checkbox_container">
          <a-checkbox v-model:checked="formState.keepLogin">Keep logged in</a-checkbox>
        </span>
        <router-link class="forget_password" to="/" @click.prevent="openForgetPasswordModal">
          Forget Password？
        </router-link>
      </div>
      <a-button type="primary" @click.prevent="handleSubmit" class="submit_btn" :disabled="isSubmitDisabled">
        Login
      </a-button>
    </a-form>
  </a-modal>
</template>

<script setup>
import { message } from 'ant-design-vue';
import Cookies from 'js-cookie';
import { ref, defineExpose, defineEmits, reactive, toRaw, watch } from 'vue';
import { useStore } from 'vuex';

import EmailInputItem from './EmailInputItem.vue';

import { loginSubmit } from '@/api/account/login.js';

const store = useStore();

const visible = ref(false);
const activeTab = ref('seeker');
const isEdge = ref(/Edg/.test(navigator.userAgent));

watch(
  () => store.state.currentAccount.type,
  (newType) => {
    activeTab.value = newType;
  },
  { immediate: true }
);

const emit = defineEmits(['open-register', 'open-forgetPassword']);
const formRef = ref(null);
const formState = reactive({
  loginName: '',
  password: '',
  loginDevice: 1, //1为电脑端，2位手机端，3为app端
  type: 'seeker',
  keepLogin: true,
});
const isSubmitDisabled = ref(false);

const handleChangeEmail = (email) => {
  formState.loginName = email;
};
const handleCancel = () => {
  visible.value = false;
};

const openForgetPasswordModal = () => {
  visible.value = false;
  emit('open-forgetPassword');
};

const openRegisterModal = () => {
  visible.value = false;
  emit('open-register');
};

const open = () => {
  visible.value = true;
};

const handleSubmit = () => {
  formRef.value.validate().then(() => {
    isSubmitDisabled.value = true;
    formState.type = activeTab.value;
    loginSubmit(toRaw(formState)).then((res) => {
      isSubmitDisabled.value = false;
      if (res.code == 0) {
        message.success(res.msg);
        localStorage.setItem('hasAccount', '1');
        store.dispatch('login', res.data.token);
        store.commit('setCurrentAccount', res.data);
        handleCancel();
        if (formState.keepLogin) {
          Cookies.set('token', res.data.token, { expires: 30 });
        } else {
          Cookies.set('token', res.data.token);
        }
      } else {
        message.error(res.msg);
      }
    });
  });
};

defineExpose({
  open,
});
</script>

<style lang="less">
.login_modal {
  color: #05644f;
  font-size: 16px;
  .ant-modal-content {
    border-radius: 20px;
    padding: 32px;
  }
  .ant-modal-title {
    color: #05644f;
    font-size: 20px;
    font-weight: bold;
    line-height: normal;
  }
  .ant-modal-close-icon {
    color: #05644f;
  }
  .tip {
    font-weight: 300;
    margin-bottom: 25px;
    color: #888;
    .link {
      font-weight: bold;
      color: #5271ff;
      margin-left: 10px;
    }
  }
  .ant-tabs-nav {
    margin-bottom: 24px;
    &::before {
      display: none;
    }
  }
  .ant-tabs-nav-list {
    width: 100%;
    padding: 2px;
    border: 1px solid rgba(5, 100, 79, 0.5);
    box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
    border-radius: 100px;
    .ant-tabs-tab {
      flex: 1;
      text-align: center;
      display: block;
      .ant-tabs-tab-btn {
        color: #05644f;
        font-size: 18px;
        font-weight: bold;
        line-height: normal;
      }
      &.ant-tabs-tab-active {
        border-radius: 100px;
        background: #05644f;
        .ant-tabs-tab-btn {
          color: #fff;
        }
      }
    }
    .ant-tabs-ink-bar {
      display: none;
    }
  }
  .ant-tabs-nav-operations {
    display: none !important;
  }
  .modal_form {
    .ant-input,
    .ant-input-password {
      border: 1px solid rgba(5, 100, 79, 0.5);
      box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
      border-radius: 16px;
      padding: 10px 24px;
      font-size: 14px;
      color: #05644f;
    }
    .ant-input-password {
      padding: 0 10px 0 0;
      .ant-input {
        border: none;
      }
    }
    .ant-input::placeholder {
      color: rgba(5, 100, 79, 0.33);
    }
    .ant-input-password-icon,
    .ant-input-password-icon:hover {
      color: #05644f;
      font-size: 18px;
    }
    .ant-form-item-explain-error {
      margin-bottom: 24px;
    }
  }
  .custom_checkbox_container {
    .ant-checkbox-wrapper {
      .ant-checkbox + span {
        color: #05644f;
      }
      .ant-checkbox .ant-checkbox-inner {
        border-radius: 10px;
        border-color: #05644f;
      }
      .ant-checkbox-checked:not(.ant-checkbox-disabled):after {
        border-color: #05644f !important;
        border-radius: 10px;
      }
      &:hover .ant-checkbox-inner,
      .ant-checkbox-checked .ant-checkbox-inner {
        background-color: #05644f;
        color: #ffffff;
      }
    }
    .ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover
      .ant-checkbox-checked:not(.ant-checkbox-disabled)
      .ant-checkbox-inner {
      background-color: #05644f;
      border-radius: 10px;
    }
  }
  .login_options {
    position: relative;
    margin-bottom: 20px;
    color: #1890ff;
  }
  .forget_password {
    cursor: pointer;
    float: right;
    font-weight: 500;
    color: #0256ff;
  }
  .submit_btn {
    background: #05644f;
    border-radius: 100px;
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    line-height: normal;
    display: block;
    width: 100%;
    padding: 10px 20px;
    height: auto;
    &:hover {
      background: #05644f;
      color: #fff;
    }
  }
}
</style>
